import { HoursOfOperation, ServiceSpecificHours } from '../models';

/**
 * Reformats an API time (2-digit 12-hour, no space for am/pm text)
 * to a UI time (1-digit 12-hour, space for am/pm text).
 * e.g. "08:00am" to "8:00 am"
 */
export const reformatApiTime = (apiTime: string): string => {
  if (!apiTime) return '';
  const AM_PM_INDEX = 5;
  // Step 1: Add the space
  const timeWithSpace =
    apiTime.slice(0, AM_PM_INDEX) + ' ' + apiTime.slice(AM_PM_INDEX);
  // Step 2: If it starts with 0, trim it
  return timeWithSpace.startsWith('0') ? timeWithSpace.slice(1) : timeWithSpace;
};

/** Mapping of days from the API to index numbers */
const DAY_TO_NUMBER: Record<string, number> = {
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
  Sun: 7,
};

/** Map of index numbers to UI day names */
const NUMBER_TO_DAY: Record<number, string> = {
  1: 'Mon',
  2: 'Tue',
  3: 'Wed',
  4: 'Thu',
  5: 'Fri',
  6: 'Sat',
  7: 'Sun',
};

/**
 * Converts days from the API (e.g. "Mon,Tue,Wed,Fri") into summarized
 * UI-friendly versions (e.g. "Mon – Wed, Fri") by bundling consecutive days.
 */
export const formatApiDays = (apiDays: string): string => {
  if (!apiDays) return '';

  /** Parsed and converted to numbers days, in order */
  const apiDayNumbers = apiDays
    .split(',')
    .map((day) => DAY_TO_NUMBER[day.trim()])
    .filter(Boolean)
    .sort((a, b) => a - b);

  if (apiDayNumbers.length === 0) return '';

  /** Each entry is a start and end day, so days can be bundled */
  const dayNumberBundles: [number, number][] = [
    [apiDayNumbers[0], apiDayNumbers[0]],
  ];

  apiDayNumbers.forEach((dayNumber) => {
    const latestBundle = dayNumberBundles[dayNumberBundles.length - 1];
    if (latestBundle[1] === dayNumber) return;

    if (latestBundle[1] === dayNumber - 1) {
      latestBundle[1] = dayNumber;
    } else {
      dayNumberBundles.push([dayNumber, dayNumber]);
    }
  });

  // Convert each bundle back into day names
  const shortDays: string[] = [];
  dayNumberBundles.forEach((bundle) => {
    if (bundle[0] === bundle[1]) {
      shortDays.push(NUMBER_TO_DAY[bundle[0]]);
    } else if (bundle[0] === bundle[1] - 1) {
      // Don't summarize if it's just 2 days in a row
      shortDays.push(
        NUMBER_TO_DAY[bundle[0]] + ', ' + NUMBER_TO_DAY[bundle[1]]
      );
    } else {
      shortDays.push(
        NUMBER_TO_DAY[bundle[0]] + ' – ' + NUMBER_TO_DAY[bundle[1]]
      );
    }
  });

  return shortDays.join(', ');
};

export interface ApiHours {
  days: string;
  start: string;
  end: string;
}

export interface ApiServiceSpecificHours {
  serviceName: string;
  hours: ApiHours[];
}

/**
 * Converts a whole API hours object to UI format.
 */
export const mapApiHours = (apiHours: ApiHours): HoursOfOperation => ({
  days: formatApiDays(apiHours.days),
  daysArray: apiHours.days.split(',').map((day) => day.trim()),
  start: reformatApiTime(apiHours.start),
  end: reformatApiTime(apiHours.end),
});

/**
 * Deep equality check for hours arrays (simple version)
 */
const areHoursEqual = (hours1: ApiHours[], hours2: ApiHours[]): boolean => {
  if (hours1.length !== hours2.length) return false;
  return hours1.every((h1, index) => {
    const h2 = hours2[index];
    return h1.days === h2.days && h1.start === h2.start && h1.end === h2.end;
  });
};

/**
 * Converts the API service specific hours to UI service specific hours.
 * Filters out hours that are identical to regular hours of operation or empty.
 */
export const mapApiServiceSpecificHours = (
  apiHoursOfOperation: ApiHours[],
  apiServiceSpecificHours: ApiServiceSpecificHours[] | undefined
): ServiceSpecificHours[] => {
  const serviceSpecificHours: ServiceSpecificHours[] = [];

  if (!apiServiceSpecificHours) return serviceSpecificHours;

  apiServiceSpecificHours.forEach((ssh) => {
    // Skip if empty
    if (!ssh.hours || ssh.hours.length === 0) return;

    // Skip if identical to regular hours of operation
    if (areHoursEqual(apiHoursOfOperation, ssh.hours)) return;

    serviceSpecificHours.push({
      serviceName: ssh.serviceName,
      hours: ssh.hours.map(mapApiHours),
    });
  });

  return serviceSpecificHours;
};
